import { State, Action } from './types';

/**
 * Initial state for VideoChatContent component
 */
export const initialState: State = {
  self: {
    video: {
      videoSizeRatio: 50
    },
    uid: ''
  },
  peer: {
    uid: ''
  },
  chat: {
    chatChannel: undefined,
    messages: [],
    chatOpen: true,
    autoTranslate: false
  },
  video: {
    drawerOpen: false,
    currentLayout: 'default',
    swapPositions: false
  },
  sessionActive: false,
  reports: []
};

/**
 * Reducer for VideoChatContent component state
 */
export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'ADD_MESSAGE':
      return {
        ...state,
        chat: {
          ...state.chat,
          messages: [...state.chat.messages, action.payload]
        }
      };

    case 'SHOW_TRANSLATION':
      return {
        ...state,
        chat: {
          ...state.chat,
          messages: state.chat.messages.map(
            message => message.id === action.payload.messageId
              ? { ...message, showTranslation: !message.showTranslation }
              : message
          )
        }
      };

    case 'UPDATE_MESSAGE_TRANSLATION':
      return {
        ...state,
        chat: {
          ...state.chat,
          messages: state.chat.messages.map(
            message => message.id === action.payload.messageId
              ? {
                ...message,
                translation: action.payload.translation,
                showTranslation: true
              }
              : message
          )
        }
      };

    case 'SET_VIDEO_SIZE_RATIO':
      return {
        ...state,
        self: {
          ...state.self,
          video: {
            ...state.self.video,
            videoSizeRatio: action.payload
          }
        }
      };

    case 'TOGGLE_CHAT':
      return {
        ...state,
        chat: {
          ...state.chat,
          chatOpen: action.payload !== undefined ? action.payload : !state.chat.chatOpen
        }
      };

    case 'SET_DRAWER_OPEN':
      return {
        ...state,
        video: {
          ...state.video,
          drawerOpen: action.payload
        }
      };

    case 'CHANGE_LAYOUT': {
      const newLayout = state.video.currentLayout === 'default'
        ? 'picture-in-picture'
        : 'default';

      // When switching back to default layout, reset the video size ratio to default 50%
      // and reset the swapPositions to ensure consistent video positioning
      if (newLayout === 'default') {
        return {
          ...state,
          self: {
            ...state.self,
            video: {
              ...state.self.video,
              videoSizeRatio: 50
            }
          },
          video: {
            ...state.video,
            currentLayout: newLayout,
            swapPositions: false
          }
        };
      }

      console.log('Changing layout to:', newLayout);
      return {
        ...state,
        video: {
          ...state.video,
          currentLayout: newLayout
        }
      };
    }

    case 'SWAP_POSITIONS':
      return {
        ...state,
        video: {
          ...state.video,
          swapPositions: !state.video.swapPositions
        }
      };

    case 'TOGGLE_AUTO_TRANSLATE':
      return {
        ...state,
        chat: {
          ...state.chat,
          autoTranslate: !state.chat.autoTranslate
        }
      };

    case 'SET_CHAT_CHANNEL':
      return {
        ...state,
        chat: {
          ...state.chat,
          chatChannel: action.payload
        }
      };

    case 'SET_SESSION_ACTIVE':
      return {
        ...state,
        sessionActive: action.payload
      };

    case 'ADD_REPORT':
      return {
        ...state,
        reports: [...state.reports, action.payload]
      };

    case 'FLUSH_REPORTS':
      return {
        ...state,
        reports: []
      };

    case 'SET_PEER_DETAILS':
      return {
        ...state,
        peer: {
          ...state.peer,
          uid: action.payload
        }
      };

    case 'SET_USER_DATA':
      return {
        ...state,
        self: {
          ...state.self,
          uid: action.payload
        }
      };

    case 'RESET':
      state.chat.chatChannel?.close();
      return {
        ...initialState,
        video: {
          ...state.video,
          currentLayout: state.video.currentLayout,
          swapPositions: state.video.swapPositions
        },
        chat: {
          ...initialState.chat,
          chatOpen: state.chat.chatOpen
        },
        sessionActive: state.sessionActive,
        reports: state.reports, // Preserve reports when resetting other state
        self: {
          ...initialState.self,
          uid: state.self.uid
        }
      };

    default:
      return state;
  }
}
